import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Reports.css';
import Icon from './Icon';
import InvoiceReadyGauge from './InvoiceReadyGauge';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';

const INVOICE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'true', label: 'Ready for invoice' },
  { value: 'false', label: 'Not marked' }
];

/** TotalTimeSpend in DB/API is always seconds (Zendesk custom field). */
const SECONDS_PER_HOUR = 3600;

function normalizeSeconds(value) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return 0;
  return Number(value);
}

function secondsToHours(seconds) {
  return normalizeSeconds(seconds) / SECONDS_PER_HOUR;
}

function getInvoiceReadyPercent(totalTimeSpend, ready4InvoiceTimeSpend) {
  const total = normalizeSeconds(totalTimeSpend);
  if (total <= 0) return 0;
  return Math.round((normalizeSeconds(ready4InvoiceTimeSpend) / total) * 100);
}

function formatHours(seconds) {
  const hours = secondsToHours(seconds);
  return `${hours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })} h`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatDateInputValue(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const emptySummary = () => ({
  ticketCount: 0,
  totalTimeSpend: 0,
  ready4InvoiceCount: 0,
  notReady4InvoiceCount: 0,
  ready4InvoiceTimeSpend: 0
});

const emptyAgreement = () => ({
  tag: null,
  description: 'Customer not categorized!',
  categorized: false
});

function getAgreementTypeClass(agreement) {
  const tag = agreement?.tag;
  if (tag === 'solid_aftale') return 'solid-aftale';
  if (tag === 'solid_aftale_light') return 'solid-aftale-light';
  if (tag === 'ad_hoc') return 'ad-hoc';
  return 'uncategorized';
}

function AgreementBadge({ agreement }) {
  if (!agreement) return null;
  return (
    <span className={`reports-agreement-badge agreement-${getAgreementTypeClass(agreement)}`}>
      {agreement.description}
    </span>
  );
}

const Reports = () => {
  const { authenticatedFetch, selectedTenant, user } = useAuth();
  const isFinance = (user?.role || '').toLowerCase() === 'finance';
  const today = useMemo(() => new Date(), []);
  const thirtyDaysAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  }, []);

  const [viewMode, setViewMode] = useState('detail');
  const [tenants, setTenants] = useState([]);
  const [tenantName, setTenantName] = useState('');
  const [dateFrom, setDateFrom] = useState(() => formatDateInputValue(thirtyDaysAgo));
  const [dateTo, setDateTo] = useState(() => formatDateInputValue(today));
  const [dateField, setDateField] = useState('updated');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(emptySummary());
  const [tickets, setTickets] = useState([]);
  const [overviewRows, setOverviewRows] = useState([]);
  const [overviewTotals, setOverviewTotals] = useState(emptySummary());
  const [agreement, setAgreement] = useState(emptyAgreement());
  const [portalUsers, setPortalUsers] = useState([]);
  const [portalUsersLoading, setPortalUsersLoading] = useState(false);
  const [portalUsersError, setPortalUsersError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ column: 'totalTimeSpend', direction: 'desc' });
  const [portalUserSort, setPortalUserSort] = useState({ column: 'lastLogin', direction: 'desc' });
  const [portalUsersExpanded, setPortalUsersExpanded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!isFinance) return;
    const loadTenants = async () => {
      try {
        const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenants`);
        const data = await response.json();
        if (response.ok && Array.isArray(data.tenants)) {
          setTenants(data.tenants);
        }
      } catch (err) {
        console.error('Failed to load tenants for reports:', err);
      }
    };
    loadTenants();
  }, [authenticatedFetch, isFinance]);

  useEffect(() => {
    if (selectedTenant?.name) {
      setTenantName(selectedTenant.name);
    }
  }, [selectedTenant?.name]);

  useEffect(() => {
    if (!selectedTenant?.name && tenants.length > 0) {
      setTenantName((current) => current || tenants[0].name);
    }
  }, [selectedTenant?.name, tenants]);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    params.set('dateField', dateField);
    if (viewMode === 'detail') {
      if (tenantName) params.set('tenant', tenantName);
      if (invoiceFilter !== 'all') params.set('ready4invoice', invoiceFilter);
      if (searchQuery.trim()) params.set('q', searchQuery.trim());
    }
    return params.toString();
  }, [dateFrom, dateTo, dateField, viewMode, tenantName, invoiceFilter, searchQuery]);

  const loadReport = useCallback(async () => {
    if (!isFinance) return;
    setLoading(true);
    setError(null);
    try {
      const query = buildQueryString();
      const endpoint =
        viewMode === 'overview'
          ? `${getApiBaseUrl()}/api/zendesk-tickets/overview?${query}`
          : `${getApiBaseUrl()}/api/zendesk-tickets?${query}`;

      if (viewMode === 'detail' && !tenantName) {
        setSummary(emptySummary());
        setTickets([]);
        setAgreement(emptyAgreement());
        setLoading(false);
        return;
      }

      const response = await authenticatedFetch(endpoint);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load report');
      }

      if (viewMode === 'overview') {
        setOverviewRows(data.tenants || []);
        setOverviewTotals(data.totals || emptySummary());
        setSummary(data.totals || emptySummary());
        setTickets([]);
        setAgreement(emptyAgreement());
      } else {
        setSummary(data.summary || emptySummary());
        setTickets(data.tickets || []);
        setAgreement(data.agreement || emptyAgreement());
        setOverviewRows([]);
        setOverviewTotals(emptySummary());
      }
    } catch (err) {
      setError(err.message || 'Failed to load report');
      setSummary(emptySummary());
      setTickets([]);
      setOverviewRows([]);
      setOverviewTotals(emptySummary());
      setAgreement(emptyAgreement());
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, buildQueryString, isFinance, tenantName, viewMode]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const loadPortalUsers = useCallback(async () => {
    if (!isFinance || viewMode !== 'detail' || !tenantName) {
      setPortalUsers([]);
      setPortalUsersError(null);
      return;
    }

    setPortalUsersLoading(true);
    setPortalUsersError(null);
    try {
      const params = new URLSearchParams({ tenant: tenantName });
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/reports/tenant-portal-users?${params.toString()}`
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load portal users');
      }
      setPortalUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      setPortalUsers([]);
      setPortalUsersError(err.message || 'Failed to load portal users');
    } finally {
      setPortalUsersLoading(false);
    }
  }, [authenticatedFetch, isFinance, tenantName, viewMode]);

  useEffect(() => {
    loadPortalUsers();
  }, [loadPortalUsers]);

  useEffect(() => {
    setPortalUsersExpanded(false);
  }, [tenantName]);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: column === 'subject' || column === 'tenant' || column === 'agreement' ? 'asc' : 'desc' };
    });
  };

  const getSortIndicator = (column) => {
    if (sortConfig.column !== column) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedTickets = useMemo(() => {
    const rows = [...tickets];
    const { column, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;

    rows.sort((a, b) => {
      let aVal;
      let bVal;
      switch (column) {
        case 'zendeskId':
          aVal = Number(a.zendeskId) || 0;
          bVal = Number(b.zendeskId) || 0;
          break;
        case 'totalTimeSpend':
          aVal = normalizeSeconds(a.totalTimeSpend);
          bVal = normalizeSeconds(b.totalTimeSpend);
          break;
        case 'ready4Invoice':
          aVal = a.ready4Invoice ? 1 : 0;
          bVal = b.ready4Invoice ? 1 : 0;
          break;
        case 'createdAt':
        case 'updatedAt':
          aVal = new Date(a[column] || 0).getTime() || 0;
          bVal = new Date(b[column] || 0).getTime() || 0;
          break;
        default:
          aVal = String(a[column] || '').toLowerCase();
          bVal = String(b[column] || '').toLowerCase();
      }
      if (aVal < bVal) return -1 * factor;
      if (aVal > bVal) return 1 * factor;
      return 0;
    });
    return rows;
  }, [tickets, sortConfig]);

  const handlePortalUserSort = (column) => {
    setPortalUserSort((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return {
        column,
        direction: column === 'username' ? 'asc' : 'desc'
      };
    });
  };

  const getPortalUserSortIndicator = (column) => {
    if (portalUserSort.column !== column) return '';
    return portalUserSort.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedPortalUsers = useMemo(() => {
    const rows = [...portalUsers];
    const { column, direction } = portalUserSort;
    const factor = direction === 'asc' ? 1 : -1;

    rows.sort((a, b) => {
      if (column === 'lastLogin') {
        const aVal = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
        const bVal = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
        if (aVal < bVal) return -1 * factor;
        if (aVal > bVal) return 1 * factor;
        return 0;
      }
      const aVal = String(a[column] || '').toLowerCase();
      const bVal = String(b[column] || '').toLowerCase();
      if (aVal < bVal) return -1 * factor;
      if (aVal > bVal) return 1 * factor;
      return 0;
    });
    return rows;
  }, [portalUsers, portalUserSort]);

  const sortedOverviewRows = useMemo(() => {
    const rows = overviewRows.map((row) => ({
      tenant: row.tenant,
      agreement: row.agreement || emptyAgreement(),
      invoiceReadyPercent: getInvoiceReadyPercent(
        row.summary?.totalTimeSpend,
        row.summary?.ready4InvoiceTimeSpend
      ),
      ...row.summary
    }));
    const { column, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (column === 'tenant') {
        const aVal = String(a.tenant || '').toLowerCase();
        const bVal = String(b.tenant || '').toLowerCase();
        if (aVal < bVal) return -1 * factor;
        if (aVal > bVal) return 1 * factor;
        return 0;
      }
      if (column === 'agreement') {
        const aVal = String(a.agreement?.description || '').toLowerCase();
        const bVal = String(b.agreement?.description || '').toLowerCase();
        if (aVal < bVal) return -1 * factor;
        if (aVal > bVal) return 1 * factor;
        return 0;
      }
      if (column === 'invoiceReadyPercent') {
        const aVal = Number(a.invoiceReadyPercent) || 0;
        const bVal = Number(b.invoiceReadyPercent) || 0;
        if (aVal < bVal) return -1 * factor;
        if (aVal > bVal) return 1 * factor;
        return 0;
      }
      const aVal = Number(a[column]) || 0;
      const bVal = Number(b[column]) || 0;
      if (aVal < bVal) return -1 * factor;
      if (aVal > bVal) return 1 * factor;
      return 0;
    });
    return rows;
  }, [overviewRows, sortConfig]);

  const activeSummary = viewMode === 'overview' ? overviewTotals : summary;

  if (!isFinance) {
    return (
      <section className="reports-page">
        <div className="reports-header">
          <h1>Reports</h1>
          <p>Only finance users can access this page.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="reports-page">
      <header className="reports-header">
        <div>
          <h1>Zendesk tickets</h1>
          <p>Invoicing report from synced tenant ticket data (read-only)</p>
        </div>
        <button
          type="button"
          className="reports-refresh"
          onClick={() => {
            loadReport();
            loadPortalUsers();
          }}
          disabled={loading || portalUsersLoading}
        >
          <Icon name="refresh" size="small" />
          {loading || portalUsersLoading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      <div className="reports-toolbar">
        <div className="reports-toolbar-group">
          <span className="reports-toolbar-label">View</span>
          <div className="reports-toggle">
            <button
              type="button"
              className={viewMode === 'detail' ? 'active' : ''}
              onClick={() => setViewMode('detail')}
            >
              Tenant detail
            </button>
            <button
              type="button"
              className={viewMode === 'overview' ? 'active' : ''}
              onClick={() => setViewMode('overview')}
            >
              All tenants
            </button>
          </div>
        </div>

        {viewMode === 'detail' && (
          <div className="reports-toolbar-group">
            <label className="reports-toolbar-label" htmlFor="reports-tenant">Tenant</label>
            <select
              id="reports-tenant"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
            >
              {tenants.map((tenant) => (
                <option key={tenant.id || tenant.name} value={tenant.name}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="reports-toolbar-group">
          <label className="reports-toolbar-label" htmlFor="reports-from">From</label>
          <input
            id="reports-from"
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <label className="reports-toolbar-label" htmlFor="reports-to">To</label>
          <input
            id="reports-to"
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
          <span className="reports-toolbar-label">by</span>
          <select
            value={dateField}
            onChange={(e) => setDateField(e.target.value)}
            aria-label="Date field"
          >
            <option value="updated">Updated</option>
            <option value="created">Created</option>
            <option value="combined">Combined</option>
          </select>
        </div>

        {viewMode === 'detail' && (
          <>
            <div className="reports-toolbar-group">
              <label className="reports-toolbar-label" htmlFor="reports-invoice">Invoice status</label>
              <select
                id="reports-invoice"
                value={invoiceFilter}
                onChange={(e) => setInvoiceFilter(e.target.value)}
              >
                {INVOICE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="reports-toolbar-group reports-search-group">
              <label className="reports-toolbar-label" htmlFor="reports-search">Search</label>
              <input
                id="reports-search"
                type="search"
                placeholder="Subject, requester, Zendesk ID…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="reports-alert">
          <Icon name="exclamation" size="small" />
          {error}
        </div>
      )}

      {viewMode === 'detail' && tenantName && (
        <div className={`reports-agreement-card agreement-${getAgreementTypeClass(agreement)}`}>
          {agreement.description}
        </div>
      )}

      <div className="reports-summary-grid">
        <div className="reports-summary-card">
          <span className="reports-summary-label">Tickets</span>
          <strong>{activeSummary.ticketCount}</strong>
        </div>
        <div className="reports-summary-card">
          <span className="reports-summary-label">Total time spent</span>
          <strong>{formatHours(activeSummary.totalTimeSpend)}</strong>
        </div>
        <div className="reports-summary-card">
          <span className="reports-summary-label">Ready for invoice</span>
          <strong>{activeSummary.ready4InvoiceCount}</strong>
        </div>
        <div className="reports-summary-card">
          <span className="reports-summary-label">Invoice-ready time</span>
          <strong>{formatHours(activeSummary.ready4InvoiceTimeSpend)}</strong>
        </div>
        <div className="reports-summary-card">
          <span className="reports-summary-label">Not marked</span>
          <strong>{activeSummary.notReady4InvoiceCount}</strong>
        </div>
      </div>

      {loading ? (
        <div className="reports-loading">
          <div className="loading-spinner" />
          <p>Loading report…</p>
        </div>
      ) : viewMode === 'overview' ? (
        <div className="reports-table-wrap">
          <table className="reports-table">
            <thead>
              <tr>
                <th><button type="button" onClick={() => handleSort('tenant')}>Tenant{getSortIndicator('tenant')}</button></th>
                <th><button type="button" onClick={() => handleSort('agreement')}>Agreement{getSortIndicator('agreement')}</button></th>
                <th><button type="button" onClick={() => handleSort('ticketCount')}>Tickets{getSortIndicator('ticketCount')}</button></th>
                <th><button type="button" onClick={() => handleSort('totalTimeSpend')}>Time spent{getSortIndicator('totalTimeSpend')}</button></th>
                <th><button type="button" onClick={() => handleSort('ready4InvoiceCount')}>Ready for invoice{getSortIndicator('ready4InvoiceCount')}</button></th>
                <th><button type="button" onClick={() => handleSort('ready4InvoiceTimeSpend')}>Invoice-ready time{getSortIndicator('ready4InvoiceTimeSpend')}</button></th>
                <th><button type="button" onClick={() => handleSort('invoiceReadyPercent')}>Invoice-ready %{getSortIndicator('invoiceReadyPercent')}</button></th>
                <th><button type="button" onClick={() => handleSort('notReady4InvoiceCount')}>Not marked{getSortIndicator('notReady4InvoiceCount')}</button></th>
              </tr>
            </thead>
            <tbody>
              {sortedOverviewRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="reports-empty">No tenant data for the selected period.</td>
                </tr>
              ) : (
                sortedOverviewRows.map((row) => (
                  <tr key={row.tenant}>
                    <td>{row.tenant}</td>
                    <td><AgreementBadge agreement={row.agreement} /></td>
                    <td>{row.ticketCount}</td>
                    <td>{formatHours(row.totalTimeSpend)}</td>
                    <td>{row.ready4InvoiceCount}</td>
                    <td>{formatHours(row.ready4InvoiceTimeSpend)}</td>
                    <td className="reports-gauge-cell">
                      <InvoiceReadyGauge
                        compact
                        totalTimeSpend={row.totalTimeSpend}
                        ready4InvoiceTimeSpend={row.ready4InvoiceTimeSpend}
                      />
                    </td>
                    <td>{row.notReady4InvoiceCount}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="reports-table-wrap">
          <table className="reports-table">
            <thead>
              <tr>
                <th><button type="button" onClick={() => handleSort('zendeskId')}>Zendesk ID{getSortIndicator('zendeskId')}</button></th>
                <th><button type="button" onClick={() => handleSort('subject')}>Subject{getSortIndicator('subject')}</button></th>
                <th><button type="button" onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</button></th>
                <th><button type="button" onClick={() => handleSort('requester')}>Requester{getSortIndicator('requester')}</button></th>
                <th><button type="button" onClick={() => handleSort('createdAt')}>Created{getSortIndicator('createdAt')}</button></th>
                <th><button type="button" onClick={() => handleSort('totalTimeSpend')}>Time spent{getSortIndicator('totalTimeSpend')}</button></th>
                <th><button type="button" onClick={() => handleSort('ready4Invoice')}>Ready for invoice{getSortIndicator('ready4Invoice')}</button></th>
              </tr>
            </thead>
            <tbody>
              {sortedTickets.length === 0 ? (
                <tr>
                  <td colSpan={7} className="reports-empty">No tickets found for the selected filters.</td>
                </tr>
              ) : (
                sortedTickets.map((ticket) => (
                  <tr key={ticket.id || ticket.zendeskId} className={ticket.ready4Invoice ? 'invoice-ready-row' : ''}>
                    <td>{ticket.zendeskId ?? '—'}</td>
                    <td className="reports-subject" title={ticket.subject}>{ticket.subject || '—'}</td>
                    <td>{ticket.status || '—'}</td>
                    <td>{ticket.requester || '—'}</td>
                    <td>{formatDateTime(ticket.createdAt)}</td>
                    <td>{formatHours(ticket.totalTimeSpend)}</td>
                    <td>
                      <span className={`reports-badge ${ticket.ready4Invoice ? 'yes' : 'no'}`}>
                        {ticket.ready4Invoice ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === 'detail' && tenantName && (
        <section className={`reports-subsection reports-subsection-collapsible${portalUsersExpanded ? ' expanded' : ''}`}>
          <button
            type="button"
            className="reports-subsection-toggle"
            onClick={() => setPortalUsersExpanded((prev) => !prev)}
            aria-expanded={portalUsersExpanded}
          >
            <div className="reports-subsection-header">
              <div>
                <h2>
                  Portal users
                  {!portalUsersLoading && (
                    <span className="reports-subsection-count"> ({portalUsers.length})</span>
                  )}
                </h2>
                <p>Users with access to {tenantName} and their last portal sign-in</p>
              </div>
              <Icon name={portalUsersExpanded ? 'arrowUp' : 'arrowDown'} size="small" />
            </div>
          </button>
          {portalUsersExpanded && (
            <div className="reports-subsection-body">
              {portalUsersError && (
                <div className="reports-alert">
                  <Icon name="exclamation" size="small" />
                  {portalUsersError}
                </div>
              )}
              {portalUsersLoading ? (
                <div className="reports-loading reports-loading-compact">
                  <div className="loading-spinner" />
                  <p>Loading portal users…</p>
                </div>
              ) : (
                <div className="reports-table-wrap">
                  <table className="reports-table">
                    <thead>
                      <tr>
                        <th><button type="button" onClick={() => handlePortalUserSort('username')}>User{getPortalUserSortIndicator('username')}</button></th>
                        <th><button type="button" onClick={() => handlePortalUserSort('lastLogin')}>Last logon{getPortalUserSortIndicator('lastLogin')}</button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPortalUsers.length === 0 ? (
                        <tr>
                          <td colSpan={2} className="reports-empty">No portal users found for this tenant.</td>
                        </tr>
                      ) : (
                        sortedPortalUsers.map((portalUser) => (
                          <tr key={portalUser.id}>
                            <td>{portalUser.username || '—'}</td>
                            <td>{formatDateTime(portalUser.lastLogin)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </section>
  );
};

export default Reports;
